'use client';

import { bunja } from 'bunja';
import { differenceInDays, isBefore } from 'date-fns';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { getBonusExpireStatusApi } from '@flipster/apis/v1/wallet';
import { accountBunja } from '@flipster/r230522/original/account';
import { marginsBunja } from '@flipster/r230522/original/margin';
import { ordersBunja } from '@flipster/r230522/original/order';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { BonusCurrency, UsdtCurrency } from '@flipster/utils/constants/currency';
import { timestampToDate } from '@flipster/utils/datetime';
import Num, { BigNumber } from '@flipster/utils/num';

import { authBunja } from '../shared/auth/state';
import { v1CurrenciesBunja } from '../shared/currency/use-v1-currencies';
import { currentTimeBunja } from '../shared/current-time/current-time.state';

export const assetMarginBonusBunja = bunja(() => {
  const authBunjaInstance = bunja.use(authBunja);
  const { isCookieLoggedInAtom } = authBunjaInstance;
  const { currenciesKeyByCurrencyAtom } = bunja.use(v1CurrenciesBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { pendingPositionsAtom } = bunja.use(ordersBunja);
  const { walletsAtom } = bunja.use(walletsBunja);
  const { v2AccountAtom } = bunja.use(accountBunja);
  const { getMarginAtom } = bunja.use(marginsBunja);
  const { currentTimeAtom } = bunja.use(currentTimeBunja);

  const getBonusExpireStatusApiAtom = atomWithQuery((get) => {
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    return {
      queryKey: ['getBonusExpireStatusApi', isCookieLoggedIn],
      queryFn: getBonusExpireStatusApi,
      enabled: !!isCookieLoggedIn,
    };
  });

  const bonusExpirationStatusAtom = atom((get) => {
    const data = get(getBonusExpireStatusApiAtom).data?.data;
    return {
      ...data,
      expiry: data?.expiry ? timestampToDate(data.expiry) : null,
    };
  });

  const remainingBonusExpirationDaysAtom = atom((get) => {
    const bonusExpirationStatus = get(bonusExpirationStatusAtom);
    const now = get(currentTimeAtom);
    return bonusExpirationStatus.expiry ? differenceInDays(bonusExpirationStatus.expiry, now) + 1 : 0;
  });

  const isTriggerBonusExpirationWarningAtom = atom((get) => {
    const remainingBonusExpirationDays = get(remainingBonusExpirationDaysAtom);
    const bonusExpirationStatus = get(bonusExpirationStatusAtom);
    const now = get(currentTimeAtom);

    return remainingBonusExpirationDays <= 4 && isBefore(now, bonusExpirationStatus.expiry ?? now);
  });

  const bonusExpirationStatusIsLoadingAtom = atom((get) => {
    return get(getBonusExpireStatusApiAtom).isLoading;
  });

  const totalMarginBonusAtom = atom((get) => {
    const wallets = get(walletsAtom);
    return Num(wallets?.[BonusCurrency]?.walletBalanceExclWithdrawalPending ?? '0');
  });

  const allocatedMarginBonusAtom = atom((get) => {
    const wallets = get(walletsAtom);
    return Num(wallets?.[BonusCurrency]?.walletBalanceLocked ?? '0');
  });

  const availableMarginBonusAtom = atom((get) => {
    const margins = get(getMarginAtom);
    return Num(margins?.[UsdtCurrency]?.isolatedMarginBonusAvailable ?? '0');
  });

  const numberOfPositionsAtom = atom((get) => {
    const openPositions = get(openPositionsAtom);
    const pendingPositions = get(pendingPositionsAtom);
    return Object.values(openPositions ?? {}).flat().length + Object.values(pendingPositions ?? {}).flat().length;
  });

  const hasPositionsAtom = atom((get) => {
    return get(numberOfPositionsAtom) > 0;
  });

  /**
   * Margin Shortage
   * https://www.notion.so/prextech/Margin-Bonus-28ceb32ad22780e18d41f11702578e41?source=copy_link#2b9eb32ad227806588c1c30d0bae11c9
   * 
   * /// case 1. no cross margin positions (see below for each value)
   * non_bonus_available_for_margin = USDT.wallet_balance_excl_locked_by_spot_trade
   * max_bonus_cap = if bonus_reduction_ratio == 1 {
   *   AQXP.wallet_balance_excl_locked_by_spot_trade
   * } else {
   *   bonus_reduction_ratio * non_bonus_available_for_margin / (1 - bonus_reduction_ratio)
   * }
   * bonus_available_for_margin_after_expiration = 
   *   min((AQXP.wallet_balance_excl_locked_by_spot_trade - amount_expiring_in_usdt), max_bonus_cap)
   * max_margin_coverable = non_bonus_available_for_margin + bonus_available_for_margin_after_expiration
   * /// case 2. if cross margin position exist
   * max_bonus_collateral_cap = if bonus_reduction_ratio == 1 {
   *   USDT.bonusCollateral
   * } else {
   *   bonus_reduction_ratio * USDT.nonBonusCollateral / (1 - bonus_reduction_ratio)
   * }
   * bonus_available_for_collateral_current =
   *   min(USDT.bonusCollateral, max_bonus_collateral_cap)
	
   * bonus_available_for_collateral_after_expiration =
   *   min(USDT.bonusCollateral - amount_expiring_in_usdt * AQXP.collateral_rate, max_bonus_collateral_cap)
   * MarginShortage = bonus_available_for_collateral_current - bonus_available_for_collateral_after_expiration
   */
  const marginShortageAtom = atom((get) => {
    const margins = get(getMarginAtom);
    const wallets = get(walletsAtom);
    const bonusExpirationStatus = get(bonusExpirationStatusAtom);
    const openPositions = get(openPositionsAtom);

    const bonusReductionRatio = Num(get(v2AccountAtom)?.bonusReductionRatio ?? '0');
    const collateralRate = Num(get(currenciesKeyByCurrencyAtom)?.[BonusCurrency]?.collateralRate ?? '0');

    const usdtWalletBalanceExclLockedBySpotTrade = Num(
      wallets?.[UsdtCurrency]?.walletBalanceExclLockedBySpotTrade ?? '0',
    );
    const aqxpWalletBalanceExclLockedBySpotTrade = Num(
      wallets?.[BonusCurrency]?.walletBalanceExclLockedBySpotTrade ?? '0',
    );

    const reservedForIsolatedMargin = Num(margins?.[UsdtCurrency]?.reservedForIsolatedMargin ?? '0');
    const bonusCollateral = Num(margins?.[UsdtCurrency]?.bonusCollateral ?? '0');
    const nonBonusCollateral = Num(margins?.[UsdtCurrency]?.nonBonusCollateral ?? '0');

    const amountExpiringInUsdt = Num(bonusExpirationStatus.amountExpiringInUsdt ?? '0');

    const hasCrossMarginPosition = Object.values(openPositions ?? {})
      .flat()
      .some((item) => item.marginType === 'Cross');

    const nonBonusAvailableForMargin = usdtWalletBalanceExclLockedBySpotTrade;

    const maxBonusCap = bonusReductionRatio.eq('1')
      ? aqxpWalletBalanceExclLockedBySpotTrade
      : bonusReductionRatio.multipliedBy(nonBonusAvailableForMargin).dividedBy(Num(1).minus(bonusReductionRatio));

    const bonusAvailableForMarginAfterExpiration = BigNumber.min(
      aqxpWalletBalanceExclLockedBySpotTrade.minus(amountExpiringInUsdt),
      maxBonusCap,
    );

    const maxMarginCoverable = nonBonusAvailableForMargin.plus(bonusAvailableForMarginAfterExpiration);

    const marginShortageNoCrossMargin = BigNumber.max(0, reservedForIsolatedMargin.minus(maxMarginCoverable));

    const maxBonusCollateralCap = bonusReductionRatio.eq('1')
      ? bonusCollateral
      : bonusReductionRatio.multipliedBy(nonBonusCollateral).dividedBy(Num(1).minus(bonusReductionRatio));

    const bonusAvailableForCollateralCurrent = BigNumber.min(bonusCollateral, maxBonusCollateralCap);

    const bonusAvailableForCollateralAfterExpiration = BigNumber.min(
      bonusCollateral.minus(amountExpiringInUsdt.multipliedBy(collateralRate)),
      maxBonusCollateralCap,
    );

    const marginShortageCrossMargin = bonusAvailableForCollateralCurrent.minus(
      bonusAvailableForCollateralAfterExpiration,
    );

    const marginShortage = hasCrossMarginPosition ? marginShortageCrossMargin : marginShortageNoCrossMargin;

    return marginShortage;
  });

  return {
    totalMarginBonusAtom,
    allocatedMarginBonusAtom,
    availableMarginBonusAtom,
    bonusExpirationStatusAtom,
    remainingBonusExpirationDaysAtom,
    isTriggerBonusExpirationWarningAtom,
    bonusExpirationStatusIsLoadingAtom,
    numberOfPositionsAtom,
    hasPositionsAtom,
    marginShortageAtom,
  };
});
assetMarginBonusBunja.debugLabel = 'assetMarginBonusBunja';
